import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { UserPlus, Eye, EyeOff, RefreshCw, Check, Copy, ArrowLeft, Ban, MailCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { extractErrorMessage } from '@/api/http/errors'
import { useToast } from '@/components/project/Toast'
import { apiRequest } from '@/api/http/fetch'
import { getAuthConfig } from '@/api/http/auth-api'

function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&*'
  let pwd = ''
  for (let i = 0; i < 12; i++) {
    pwd += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return pwd
}

export function RegisterPage() {
  const navigate = useNavigate()
  const { toast } = useToast()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [copied, setCopied] = useState(false)
  const [configChecked, setConfigChecked] = useState(false)
  const [registrationOpen, setRegistrationOpen] = useState(true)
  const [requireEmailVerification, setRequireEmailVerification] = useState(false)
  const [registered, setRegistered] = useState(false)

  useEffect(() => {
    getAuthConfig()
      .then((cfg) => {
        setRegistrationOpen(cfg.registration_open)
        setRequireEmailVerification(cfg.require_email_verification)
      })
      .catch(() => { /* если не смогли — показываем форму */ })
      .finally(() => setConfigChecked(true))
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setEmailError('')
    setPasswordError('')

    if (!email.trim()) { setEmailError('Введите email'); return }
    if (!password) { setPasswordError('Введите пароль'); return }

    setLoading(true)
    try {
      await apiRequest('/api/v1/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, first_name: firstName || undefined, last_name: lastName || undefined }),
      })
      if (requireEmailVerification) {
        setRegistered(true)
        toast('success', 'Регистрация успешна! Проверьте email для подтверждения.')
      } else {
        toast('success', 'Регистрация успешна! Теперь войдите в систему.')
        navigate('/login', { replace: true })
      }
    } catch (err) {
      setError(extractErrorMessage(err, 'Ошибка регистрации'))
    } finally {
      setLoading(false)
    }
  }

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(password)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* ignore */ }
  }

  if (!configChecked) {
    return (
      <div className="flex items-center justify-center min-h-dvh p-4">
        <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (registered) {
    return (
      <div className="flex items-center justify-center min-h-dvh p-4">
        <div className="max-w-sm mx-auto w-full text-center">
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-green-soft text-green mb-4">
              <MailCheck className="w-6 h-6" strokeWidth={2} />
            </div>
            <h1 className="text-xl font-semibold text-text">Почти готово!</h1>
            <p className="text-sm text-text-muted mt-3 text-center leading-relaxed">
              Мы отправили письмо с подтверждением на <strong className="text-text">{email}</strong>.<br />
              Перейдите по ссылке в письме, чтобы завершить регистрацию.
            </p>
          </div>
          <Link to="/login">
            <Button className="w-full gap-2" size="lg">
              <ArrowLeft className="w-4 h-4" strokeWidth={2} />
              Вернуться к входу
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  if (!registrationOpen) {
    return (
      <div className="flex items-center justify-center min-h-dvh p-4">
        <div className="max-w-sm mx-auto w-full">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2} />
            Назад к входу
          </Link>

          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-destructive/10 text-destructive mb-4">
              <Ban className="w-5 h-5" strokeWidth={2} />
            </div>
            <h1 className="text-xl font-semibold text-text">Регистрация закрыта</h1>
            <p className="text-sm text-text-muted mt-1 text-center">
              В данный момент регистрация новых пользователей временно недоступна. Обратитесь к администратору системы.
            </p>
          </div>

          <Link to="/login">
            <Button className="w-full gap-2" size="lg">
              <ArrowLeft className="w-4 h-4" strokeWidth={2} />
              Вернуться к входу
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-dvh p-4">
      <div className="max-w-sm mx-auto w-full">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          Назад к входу
        </Link>

        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-[#8aa4ff] text-white text-base font-bold mb-4">
            DH
          </div>
          <h1 className="text-xl font-semibold text-text">Регистрация</h1>
          <p className="text-sm text-text-muted mt-1">Создайте учётную запись</p>
        </div>

        <div className="rounded-xl border border-border bg-surface p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-text">
                Email <span className="text-red">*</span>
              </label>
              <Input
                type="email"
                placeholder="mail@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError('') }}
                aria-invalid={!!emailError}
              />
              {emailError && <p className="text-xs text-red mt-1">{emailError}</p>}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-text">
                Пароль <span className="text-red">*</span>
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="··········"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setPasswordError('') }}
                    aria-invalid={!!passwordError}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={2} /> : <Eye className="w-4 h-4" strokeWidth={2} />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5 shrink-0"
                  onClick={() => { setPassword(generatePassword()); setShowPassword(true) }}
                  title="Сгенерировать пароль"
                >
                  <RefreshCw className="w-3.5 h-3.5" strokeWidth={2} />
                </Button>
                {password && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 shrink-0"
                    onClick={copyPassword}
                    title="Копировать пароль"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-green" strokeWidth={3} /> : <Copy className="w-3.5 h-3.5" strokeWidth={2} />}
                  </Button>
                )}
              </div>
              {passwordError && <p className="text-xs text-red mt-1">{passwordError}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-text">Имя</label>
                <Input
                  placeholder="Иван"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-text">Фамилия</label>
                <Input
                  placeholder="Петров"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            {error && <p className="text-sm text-red text-center">{error}</p>}

            <Button type="submit" className="w-full gap-2" size="lg" disabled={loading}>
              <UserPlus className="w-4 h-4" strokeWidth={2} />
              {loading ? 'Регистрация...' : 'Зарегистрироваться'}
            </Button>
          </form>
        </div>

        <p className="text-xs text-text-muted text-center mt-4">
          Уже есть аккаунт?{' '}
          <Link to="/login" className="text-accent hover:text-accent/70 transition-colors">
            Войти
          </Link>
        </p>
      </div>
    </div>
  )
}
